import React, { useState, useEffect, useRef } from 'react';
import { TopBar } from './features/navigation/TopBar.jsx';
import { VoiceRecorderModal } from './features/avatar/VoiceRecorderModal.jsx';
import { FaceScannerModal } from './features/avatar/FaceScannerModal.jsx';
import { CreativeDirectorModal } from './features/prompt/CreativeDirectorModal.jsx';
import { StudioSidebar } from './features/controls/StudioSidebar.jsx';
import { SplitViewCanvas } from './features/viewport/SplitViewCanvas.jsx';
import { FilmstripTimeline } from './features/viewport/FilmstripTimeline.jsx';
import { AccessDeniedView } from './features/auth/AccessDeniedView.jsx';
import { INITIAL_TAKES } from './features/controls/controlConstants.js';
import { enhancePromptWithGemini } from './features/prompt/enhancePromptAction.js';
import { executeVideoGeneration } from './features/generator/generateVideoAction.js';
import { executeAutoEdit } from './features/controls/executeAutoEditAction.js';
import { verifySuperAdminSession } from './features/auth/verifySuperAdminSession.js';
import { MobileFaceScannerView } from './features/avatar/MobileFaceScannerView.jsx';
import { MetaAdsPublisherModal } from './features/generator/MetaAdsPublisherModal.jsx';
import { MetaAnalyticsDashboard } from './features/analytics/MetaAnalyticsDashboard.jsx';

function StudioWorkspace({ user }) {

  const [activeTab, setActiveTab] = useState('video');
  const [beforeImage, setBeforeImage] = useState('/avatar_base.jpg');
  const [afterImage, setAfterImage] = useState('/avatar_upscaled.jpg');

  // Multi-Empresa / Clientes B2B
  const [clients, setClients] = useState([]);
  const [activeClientId, setActiveClientId] = useState(() => {
    return localStorage.getItem('magnific_active_client_id') || 'c-ayuntamiento';
  });

  useEffect(() => {
    fetch('/api/clients')
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.clients) {
          setClients(data.clients);
        }
      })
      .catch((err) => console.error('Error cargando empresas:', err));
  }, []);

  const activeClient = clients.find((c) => c.id === activeClientId) || clients[0] || null;

  const handleSelectClient = (id) => {
    setActiveClientId(id);
    localStorage.setItem('magnific_active_client_id', id);
  };

  const handleCreateClient = async (newClientData) => {
    try {
      const res = await fetch('/api/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newClientData)
      });
      const data = await res.json();
      if (data.success && data.client) {
        setClients((prev) => [...prev, data.client]);
        handleSelectClient(data.client.id);
      }
    } catch (err) {
      console.error('Error creando empresa:', err);
    }
  };

  const [profiles, setProfiles] = useState(() => {
    try {
      const saved = localStorage.getItem('magnific_avatar_profiles');
      if (saved) return JSON.parse(saved);
    } catch (_) {}
    return [
      {
        id: 'p-1',
        name: 'Raúl Silveira',
        avatarSrc: localStorage.getItem('magnific_avatar_image') || '/avatar_base.jpg',
        angles: [],
        voiceAudioUrl: localStorage.getItem('magnific_voice_audio') || null,
        voiceName: 'Voz Raúl Silveira'
      }
    ];
  });
  const [activeProfileId, setActiveProfileId] = useState(() => {
    return localStorage.getItem('magnific_active_profile_id') || 'p-1';
  });

  const [isDuoMode, setIsDuoMode] = useState(false);
  const [duoSecondaryId, setDuoSecondaryId] = useState('');

  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
  const [isScannerModalOpen, setIsScannerModalOpen] = useState(false);
  const [isCreativeModalOpen, setIsCreativeModalOpen] = useState(false);
  const [isMetaModalOpen, setIsMetaModalOpen] = useState(false);
  const [quality, setQuality] = useState('1080p');

  const [prompt, setPrompt] = useState('Cinematic documentary portrait of male dentist in modern dental clinic, warm confidence, wearing crisp navy medical scrubs, soft studio lighting, natural eye contact, 35mm lens, 8k');
  const [negativePrompt, setNegativePrompt] = useState('blurry, deformed face, bad anatomy, flicker, cartoon');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [motionIntensity, setMotionIntensity] = useState(6);
  const [resemblance, setResemblance] = useState(90);
  const [creativity, setCreativity] = useState(40);
  const [cameraPreset, setCameraPreset] = useState('dolly-in');
  const [ratio, setRatio] = useState('9:16');
  const [duration, setDuration] = useState('15s');
  const [fps, setFps] = useState(24);
  const [scaleFactor, setScaleFactor] = useState('4x (4K Ultra)');
  const [lightColor, setLightColor] = useState('#f59e0b');
  const [lightIntensity, setLightIntensity] = useState(70);
  const [lightDirection, setLightDirection] = useState('left');
  const [cutSilences, setCutSilences] = useState(true);
  const [neonCaptions, setNeonCaptions] = useState(true);
  const [captionAccent, setCaptionAccent] = useState('gold');
  const [entranceZoom, setEntranceZoom] = useState(true);
  const [audioMaster, setAudioMaster] = useState(true);
  const [transcriptText, setTranscriptText] = useState('Si tardas más de 5 minutos en responder por WhatsApp a un paciente, ya se fue con otro dentista. Automatiza tu recepción: responde dudas, da precios y agenda citas en segundos, incluso mientras estás en consulta. Escríbenos ahora y llena tu agenda.');
  const [karaokeWords, setKaraokeWords] = useState([
    { text: "NO", isKey: false },
    { text: "PIERDAS", isKey: true },
    { text: "PACIENTES", isKey: true },
    { text: "RESPUESTA", isKey: false },
    { text: "EN", isKey: false },
    { text: "SEGUNDOS", isKey: true },
    { text: "RECEPCIÓN", isKey: true },
    { text: "INTELIGENTE", isKey: true },
    { text: "AGENDA", isKey: false },
    { text: "LLENA", isKey: true }
  ]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [splitPos, setSplitPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSafeZones, setShowSafeZones] = useState(false);
  const [takes, setTakes] = useState(() => {
    try {
      const saved = localStorage.getItem('magnific_takes');
      return saved ? JSON.parse(saved) : INITIAL_TAKES;
    } catch {
      return INITIAL_TAKES;
    }
  });
  const containerRef = useRef(null);
  const playAnimationRef = useRef(null);

  // Perfil activo derivado
  const activeProfile = profiles.find((p) => p.id === activeProfileId) || profiles[0];
  const avatarImage = activeProfile?.avatarSrc || '/avatar_base.jpg';
  const avatarName = activeProfile?.name || 'Mi Avatar';
  const avatarAngles = activeProfile?.angles || [];
  const voiceAudioUrl = activeProfile?.voiceAudioUrl || null;
  const voiceName = activeProfile?.voiceName || `Voz de ${avatarName}`;

  // Sincronizar y cargar el perfil, prompt, guion y tomas exclusivas de la empresa activa
  useEffect(() => {
    if (!activeClient?.id) return;
    const cid = activeClient.id;

    // 1. Perfiles de la empresa
    const savedProfiles = localStorage.getItem(`magnific_profiles_${cid}`);
    if (savedProfiles) {
      try {
        const parsed = JSON.parse(savedProfiles);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setProfiles(parsed);
          const savedActivePId = localStorage.getItem(`magnific_active_profile_${cid}`);
          setActiveProfileId(savedActivePId || parsed[0].id);
        }
      } catch (_) {}
    } else if (activeClient.profiles && activeClient.profiles.length > 0) {
      setProfiles(activeClient.profiles);
      setActiveProfileId(activeClient.profiles[0].id);
    }

    // 2. Prompt exclusivo
    const savedPrompt = localStorage.getItem(`magnific_prompt_${cid}`);
    if (savedPrompt) {
      setPrompt(savedPrompt);
    } else if (activeClient.defaultPrompt) {
      setPrompt(activeClient.defaultPrompt);
    }

    // 3. Guion / Teleprompter exclusivo
    const savedScript = localStorage.getItem(`magnific_script_${cid}`);
    if (savedScript) {
      setTranscriptText(savedScript);
    } else if (activeClient.defaultScript) {
      setTranscriptText(activeClient.defaultScript);
    }

    // 4. Subtítulos de Karaoke exclusivos
    const savedCaptions = localStorage.getItem(`magnific_captions_${cid}`);
    if (savedCaptions) {
      try {
        setKaraokeWords(JSON.parse(savedCaptions));
      } catch (_) {}
    } else if (activeClient.defaultCaptions && Array.isArray(activeClient.defaultCaptions)) {
      setKaraokeWords(activeClient.defaultCaptions.map((text, idx) => ({ text, isKey: idx % 2 === 1 })));
    }

    // 5. Tomas / Historial exclusivo
    const savedTakes = localStorage.getItem(`magnific_takes_${cid}`);
    if (savedTakes) {
      try {
        setTakes(JSON.parse(savedTakes));
      } catch (_) {}
    } else if (activeClient.videoHistory && activeClient.videoHistory.length > 0) {
      setTakes(activeClient.videoHistory.map((v) => ({
        id: v.id,
        name: v.title,
        badge: v.status === 'active_delivering' ? '🟢 Activo Meta' : '15s HD',
        thumb: '/avatar_base.jpg',
        src: '/avatar_upscaled.jpg',
        isVideo: true,
        duration: v.duration || '15s',
        ratio: v.ratio || '9:16'
      })));
    }
  }, [activeClient?.id]);

  // Persistir cambios en el espacio de la empresa activa
  useEffect(() => {
    if (!activeClient?.id) return;
    const cid = activeClient.id;
    try {
      localStorage.setItem(`magnific_profiles_${cid}`, JSON.stringify(profiles));
      localStorage.setItem(`magnific_active_profile_${cid}`, activeProfileId);
    } catch (_) {}
  }, [profiles, activeProfileId, activeClient?.id]);

  useEffect(() => {
    if (!activeClient?.id) return;
    try {
      localStorage.setItem(`magnific_prompt_${activeClient.id}`, prompt);
    } catch (_) {}
  }, [prompt, activeClient?.id]);

  useEffect(() => {
    if (!activeClient?.id) return;
    try {
      localStorage.setItem(`magnific_script_${activeClient.id}`, transcriptText);
    } catch (_) {}
  }, [transcriptText, activeClient?.id]);

  useEffect(() => {
    if (!activeClient?.id) return;
    try {
      localStorage.setItem(`magnific_captions_${activeClient.id}`, JSON.stringify(karaokeWords));
    } catch (_) {}
  }, [karaokeWords, activeClient?.id]);

  useEffect(() => {
    if (!activeClient?.id) return;
    try {
      localStorage.setItem(`magnific_takes_${activeClient.id}`, JSON.stringify(takes));
    } catch (_) {}
  }, [takes, activeClient?.id]);

  const handleZoomIn = () => setZoomLevel((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100));
  const handleZoomOut = () => setZoomLevel((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100));
  const handleResetZoom = () => setZoomLevel(1);
  const handleToggleFullscreen = () => setIsFullscreen((f) => !f);


  // Animación de Play / Preview automático en canvas
  useEffect(() => {
    if (isPlaying) {
      let direction = 1;
      playAnimationRef.current = setInterval(() => {
        setSplitPos((prev) => {
          if (prev >= 85) direction = -1;
          if (prev <= 15) direction = 1;
          return prev + direction * 2;
        });
      }, 50);
    } else if (playAnimationRef.current) {
      clearInterval(playAnimationRef.current);
    }
    return () => clearInterval(playAnimationRef.current);
  }, [isPlaying]);

  const handleMagicEnhance = async () => {
    setIsEnhancing(true);
    try {
      const enhanced = await enhancePromptWithGemini(prompt);
      setPrompt(enhanced);
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenProgress(0);
    try {
      const res = await executeVideoGeneration(
        { prompt, negativePrompt, motionIntensity, resemblance, creativity, cameraPreset, ratio, duration, fps, avatarImage },
        (p) => setGenProgress(p)
      );
      if (res && res.result?.videoUrl) {
        setAfterImage(res.result.videoUrl);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExport = () => {
    const isVideo = afterImage?.endsWith('.mp4') || afterImage?.endsWith('.webm') || afterImage?.startsWith('blob:');
    const ext = afterImage?.endsWith('.mp4') ? 'mp4' : afterImage?.endsWith('.webm') ? 'webm' : isVideo ? 'mp4' : 'jpg';
    const link = document.createElement('a');
    link.href = afterImage;
    link.download = `studio_${activeTab}_take_${Date.now()}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyPrompt = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(prompt);
    }
  };

  const handleReroll = () => {
    handleGenerate();
  };

  const handleAutoEdit = async () => {
    setIsGenerating(true);
    setGenProgress(0);
    try {
      const res = await executeAutoEdit(
        {
          cutSilences,
          neonCaptions,
          captionAccent,
          entranceZoom,
          audioMaster,
          transcriptText,
          videoUrl: afterImage
        },
        (p) => setGenProgress(p)
      );
      if (res && res.pipeline?.karaokeCaptions?.previewWords) {
        setKaraokeWords(res.pipeline.karaokeCaptions.previewWords);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddVariation = () => {
    const nextId = takes.length + 1;
    const newTake = {
      id: nextId,
      name: `Take 0${nextId} - ${activeTab.toUpperCase()} Mod`,
      active: true
    };
    setTakes((prev) => [...prev.map((t) => ({ ...t, active: false })), newTake]);
    handleGenerate();
  };

  const updateSplitPosition = (clientX) => {
    if (!isDragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    setSplitPos((x / rect.width) * 100);
  };

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100vw', background: '#090a0f', overflow: 'hidden' }}
      onMouseMove={(e) => updateSplitPosition(e.clientX)}
      onMouseUp={() => setIsDragging(false)}
      onTouchMove={(e) => updateSplitPosition(e.touches[0].clientX)}
      onTouchEnd={() => setIsDragging(false)}
    >
      <TopBar
        activeTab={activeTab}
        onSelectTab={setActiveTab}
        avatarSrc={avatarImage}
        clients={clients}
        activeClientId={activeClientId}
        activeClient={activeClient}
        onSelectClient={handleSelectClient}
        onCreateClient={handleCreateClient}
      />

      {activeTab === 'meta-analytics' ? (
        <MetaAnalyticsDashboard activeClient={activeClient} />
      ) : (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <StudioSidebar
            activeTab={activeTab}
            profiles={profiles}
            activeProfileId={activeProfileId}
            setActiveProfileId={setActiveProfileId}
            setProfiles={setProfiles}
            isDuoMode={isDuoMode}
            setIsDuoMode={setIsDuoMode}
            duoSecondaryId={duoSecondaryId}
            setDuoSecondaryId={setDuoSecondaryId}
            avatarImage={avatarImage}
            avatarName={avatarName}
            avatarAngles={avatarAngles}
            voiceName={voiceName}
            voiceAudioUrl={voiceAudioUrl}
            setIsScannerModalOpen={setIsScannerModalOpen}
            setIsVoiceModalOpen={setIsVoiceModalOpen}
            setIsCreativeModalOpen={setIsCreativeModalOpen}
            prompt={prompt}
            setPrompt={setPrompt}
            negativePrompt={negativePrompt}
            setNegativePrompt={setNegativePrompt}
            handleMagicEnhance={handleMagicEnhance}
            isEnhancing={isEnhancing}
            quality={quality}
            setQuality={setQuality}
            motionIntensity={motionIntensity}
            setMotionIntensity={setMotionIntensity}
            resemblance={resemblance}
            setResemblance={setResemblance}
            creativity={creativity}
            setCreativity={setCreativity}
            cameraPreset={cameraPreset}
            setCameraPreset={setCameraPreset}
            ratio={ratio}
            setRatio={setRatio}
            duration={duration}
            setDuration={setDuration}
            fps={fps}
            setFps={setFps}
            scaleFactor={scaleFactor}
            setScaleFactor={setScaleFactor}
            lightColor={lightColor}
            setLightColor={setLightColor}
            lightIntensity={lightIntensity}
            setLightIntensity={setLightIntensity}
            lightDirection={lightDirection}
            setLightDirection={setLightDirection}
            cutSilences={cutSilences}
            setCutSilences={setCutSilences}
            neonCaptions={neonCaptions}
            setNeonCaptions={setNeonCaptions}
            captionAccent={captionAccent}
            setCaptionAccent={setCaptionAccent}
            entranceZoom={entranceZoom}
            setEntranceZoom={setEntranceZoom}
            audioMaster={audioMaster}
            setAudioMaster={setAudioMaster}
            transcriptText={transcriptText}
            setTranscriptText={setTranscriptText}
            isGenerating={isGenerating}
            genProgress={genProgress}
            handleGenerate={handleGenerate}
            handleAutoEdit={handleAutoEdit}
            afterImage={afterImage}
            setBeforeImage={setBeforeImage}
            setAfterImage={setAfterImage}
          />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <SplitViewCanvas
            beforeSrc={beforeImage || avatarImage}
            afterSrc={afterImage}
            splitPos={splitPos}
            containerRef={containerRef}
            onMouseDown={() => setIsDragging(true)}
            onTouchStart={() => setIsDragging(true)}
            onExport={handleExport}
            onSendToUpscaler={() => setActiveTab('upscaler')}
            onSendToRelight={() => setActiveTab('relight')}
            onCopyPrompt={handleCopyPrompt}
            onReroll={handleReroll}
            onLaunchMetaAds={() => setIsMetaModalOpen(true)}
            isPlaying={isPlaying}
            onTogglePlay={() => setIsPlaying(!isPlaying)}
            zoomLevel={zoomLevel}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onResetZoom={handleResetZoom}
            isFullscreen={isFullscreen}
            onToggleFullscreen={handleToggleFullscreen}
            activeLightDirection={activeTab === 'relight' ? lightDirection : null}
            karaokeWords={neonCaptions ? karaokeWords : null}
            captionAccent={captionAccent}
            entranceZoomActive={entranceZoom}
            showSafeZones={showSafeZones}
            onToggleSafeZones={() => setShowSafeZones(!showSafeZones)}
          />


          <FilmstripTimeline
            takes={takes}
            onSelectTake={(id) => setTakes((prev) => prev.map((t) => ({ ...t, active: t.id === id })))}
            onAddVariation={handleAddVariation}
            thumbnailSrc={avatarImage}
          />
        </div>
      </div>
      )}

      {/* Modal Director Creativo & Estratega de Campañas IA */}
      <CreativeDirectorModal
        isOpen={isCreativeModalOpen}
        onClose={() => setIsCreativeModalOpen(false)}
        activeClient={activeClient}
        onApplyCampaign={(campaign) => {
          if (campaign.visualPrompt) setPrompt(campaign.visualPrompt);
          if (campaign.script) setTranscriptText(campaign.script);
          if (campaign.recommendedCamera) setCameraPreset(campaign.recommendedCamera);
          if (campaign.recommendedRatio) setRatio(campaign.recommendedRatio);
          if (campaign.recommendedDuration) setDuration(campaign.recommendedDuration);
          if (campaign.captions && Array.isArray(campaign.captions)) {
            setKaraokeWords(campaign.captions.map((c) => ({ text: c, isKey: true })));
          }

          // Registrar en el historial de la empresa activa
          if (activeClient?.id) {
            fetch(`/api/clients/${activeClient.id}/videos`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                video: {
                  title: campaign.title || 'Campaña de Video',
                  type: campaign.campaignType || 'meta_ads_15s',
                  ratio: campaign.recommendedRatio || ratio,
                  duration: campaign.recommendedDuration || duration,
                  hook: campaign.hook || '',
                  script: campaign.script || '',
                  captions: campaign.captions || []
                }
              })
            })
              .then((res) => res.json())
              .then((d) => {
                if (d.success && d.video) {
                  setClients((prev) => prev.map((c) => (
                    c.id === activeClient.id
                      ? { ...c, videoHistory: [d.video, ...(c.videoHistory || [])] }
                      : c
                  )));
                }
              })
              .catch((e) => console.error('Error guardando en historial:', e));
          }
        }}
      />

      {/* Modal Calibración de Voz en Vivo con Teleprompter */}
      <VoiceRecorderModal
        isOpen={isVoiceModalOpen}
        onClose={() => setIsVoiceModalOpen(false)}
        avatarName={avatarName}
        onSaveVoice={(url) => {
          setProfiles((prev) => prev.map((p) => (
            p.id === activeProfileId ? { ...p, voiceAudioUrl: url, voiceName: `Voz de ${avatarName}` } : p
          )));
        }}
      />

      {/* Modal Escáner Facial 3D y Código QR */}
      <FaceScannerModal
        isOpen={isScannerModalOpen}
        onClose={() => setIsScannerModalOpen(false)}
        avatarName={avatarName}
        onSaveAngles={(capturedAngles) => {
          setProfiles((prev) => prev.map((p) => (
            p.id === activeProfileId ? { ...p, angles: [...(p.angles || []), ...capturedAngles] } : p
          )));
        }}
      />

      {/* Modal Lanzador de Campañas Meta Ads */}
      <MetaAdsPublisherModal
        isOpen={isMetaModalOpen}
        onClose={() => setIsMetaModalOpen(false)}
        activeClient={activeClient}
        videoData={{
          title: prompt ? `Campaña ${activeClient?.name || 'Cliente'}` : 'Video Promocional',
          script: transcriptText,
          videoUrl: afterImage,
          ratio,
          duration
        }}
      />
    </div>
  );
}

export default function App() {
  const [authStatus, setAuthStatus] = useState({ checking: true, isSuperAdmin: false, user: null });

  useEffect(() => {
    verifySuperAdminSession()
      .then((res) => {
        setAuthStatus({ checking: false, isSuperAdmin: res.isSuperAdmin, user: res.user });
      })
      .catch(() => {
        setAuthStatus({ checking: false, isSuperAdmin: false, user: null });
      });
  }, []);

  // Detección directa de Escaneo Móvil vía QR (?mobile-scan=true&session=...&avatar=...)
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const isMobileScanDirect = urlParams?.get('mobile-scan') === 'true' || urlParams?.get('mobile_scan') === 'true';
  const mobileSessionId = urlParams?.get('session') || '';
  const mobileAvatarName = urlParams?.get('avatar') || 'Mi Avatar';

  if (isMobileScanDirect) {
    return <MobileFaceScannerView sessionId={mobileSessionId} avatarName={mobileAvatarName} />;
  }

  if (authStatus.checking) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw',
        background: '#030712',
        color: '#a855f7',
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        fontWeight: 600
      }}>
        Verificando credenciales SuperAdmin Softmatek...
      </div>
    );
  }

  if (!authStatus.isSuperAdmin) {
    return (
      <AccessDeniedView
        onAuthenticated={() => setAuthStatus({ checking: false, isSuperAdmin: true, user: { role: 'SUPERADMIN' } })}
      />
    );
  }

  return <StudioWorkspace user={authStatus.user} />;
}


